import React, { useState, useRef, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { useSearchLocation } from "@/hooks/useSearchLocation"

interface CitySearchInputProps {
  value: any // selected city object
  onChange: (location: any) => void
  error?: boolean
  label?: string
  placeholder?: string
}

export function CitySearchInput({
  value,
  onChange,
  error = false,
  label = "Search your City",
  placeholder = "Type to search..."
}: CitySearchInputProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [showDropdown, setShowDropdown] = useState(false)

  const searchConfig = {
    label,
    url: "https://finance.devapi.zipaworld.com/api/auth/masters/city/manager"
  }

  const {
    searchTerm: rfqLocationSearchTerm,
    setSearchTerm: setRfqLocationSearchTerm,
    results: rfqLocationResults,
    setResults: setRfqLocationResults,
    isSearching: rfqIsSearchingLocation
  } = useSearchLocation(
    searchConfig.url,
    500,
    value?.city || value?.name || value?.pincode || value?.portName
  )

  useEffect(() => {
    if (value) {
      setRfqLocationSearchTerm(value.city || value.name || value.pincode || value.portName || "")
    } else {
      setRfqLocationSearchTerm("")
    }
  }, [value, setRfqLocationSearchTerm])

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [])

  return (
    <div ref={containerRef} className="relative space-y-1 pb-2">
      <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500 block">
        {label}
      </label>
      {value ? (
        <div className="flex items-center justify-between rounded-lg border border-primary/40 bg-primary/5 px-2.5 py-2 transition-all duration-200">
          <span className="text-xs font-bold text-slate-800">
            {value.city || value.name || value.pincode || value.portName}
          </span>
          <button
            type="button"
            onClick={() => {
              onChange(null)
              setRfqLocationSearchTerm("")
              setRfqLocationResults([])
            }}
            className="text-slate-400 hover:text-primary font-bold text-[10px] px-2 uppercase tracking-wider cursor-pointer"
          >
            Change
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            required
            value={rfqLocationSearchTerm}
            onChange={(e) => {
              setRfqLocationSearchTerm(e.target.value)
              setShowDropdown(true)
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder={placeholder}
            className={`w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200 ${
              error
                ? "border-primary ring-2 ring-primary/20"
                : "border-slate-200 focus:border-primary"
            }`}
          />
          {rfqIsSearchingLocation && (
            <Loader2 className="absolute right-2.5 top-2.5 size-4 animate-spin text-slate-400" />
          )}
          {showDropdown && rfqLocationSearchTerm.length >= 2 && rfqLocationResults.length > 0 && (
            <ul
              className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg overscroll-contain"
              onMouseDown={(e) => e.preventDefault()}
            >
              {rfqLocationResults.map((loc: any, idx: number) => (
                <li
                  key={loc._id || idx}
                  onClick={() => {
                    onChange(loc)
                    setRfqLocationSearchTerm(loc.city || loc.name || loc.pincode || loc.portName || "")
                    setShowDropdown(false)
                  }}
                  className="px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors border-b border-slate-50 last:border-0"
                >
                  {loc.city || loc.name || loc.pincode || loc.portName}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
